"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState, type FormEvent } from "react";
import { Lock, Mail } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { affApi, setAffToken } from "@/lib/affiliatePortal";
import {
  Alert,
  AuthHeading,
  AuthShell,
  Field,
  OrDivider,
  OtpInput,
  PasswordField,
  SubmitButton,
} from "@/components/auth/AuthUI";

/** Highlight so the page reads clearly as the affiliate (not the customer) sign-in. */
type Mode = "login" | "forgot" | "reset";

export default function AffiliateLoginPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const toDash = useCallback(() => router.replace("/affiliate/dashboard"), [router]);
  const showError = useCallback((m: string) => setError(m), []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      if (mode === "login") {
        const r = await affApi.login(email.trim(), password);
        setAffToken(r.token);
        router.replace("/affiliate/dashboard");
      } else if (mode === "forgot") {
        await affApi.forgotPassword(email.trim());
        setMode("reset");
        setNotice("If an affiliate account exists, we sent a reset code to your email.");
      } else {
        await affApi.resetPassword(email.trim(), code.trim(), password);
        setMode("login");
        setPassword("");
        setNotice("Password updated — sign in with your new password.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  const legal = (
    <>
      Affiliate earnings are paid out manually. See the{" "}
      <Link href="/terms" className="font-semibold underline underline-offset-2 hover:text-[#4b33e0]">Terms</Link>.
    </>
  );

  return (
    <AuthShell mode="affiliate-login" footer={legal}>
      <AuthHeading
        title={mode === "login" ? "Affiliate sign in" : mode === "forgot" ? "Reset your password" : "Set a new password"}
        sub={
          mode === "login"
            ? "Sign in to your affiliate account to see your link, stats and earnings."
            : mode === "forgot"
              ? "Enter your email and we'll send a 6-digit reset code."
              : "Enter the code we emailed and choose a new password."
        }
      />

      {mode === "login" && (
        <>
          <GoogleSignInButton
            text="signin_with"
            label="Continue with Google"
            exchange={async (cred) => { const r = await affApi.google(cred); setAffToken(r.token); }}
            onSuccess={toDash}
            onError={showError}
          />
          <OrDivider />
        </>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          id="aff-email"
          label="Email"
          icon={Mail}
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {mode === "reset" && (
          <OtpInput value={code} onChange={setCode} invalid={!!error} disabled={loading} />
        )}
        {(mode === "login" || mode === "reset") && (
          <PasswordField
            id="aff-password"
            icon={Lock}
            value={password}
            onChange={setPassword}
            autoComplete={mode === "reset" ? "new-password" : "current-password"}
            placeholder={mode === "reset" ? "At least 8 characters" : "Your password"}
            meter={mode === "reset"}
            minLength={8}
            aside={
              mode === "login" ? (
                <button type="button" onClick={() => { setMode("forgot"); setError(null); setNotice(null); }}
                  className="text-[0.78rem] font-bold text-[#4b33e0] hover:underline">Forgot password?</button>
              ) : undefined
            }
          />
        )}

        {notice && <Alert tone="success">{notice}</Alert>}
        {error && <Alert tone="error" title="Couldn't sign you in">{error}</Alert>}

        <div className="pt-1.5">
          <SubmitButton loading={loading} loadingText="Please wait…">
            {mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset code" : "Set new password"}
          </SubmitButton>
        </div>
      </form>

      {mode !== "login" && (
        <button type="button" onClick={() => { setMode("login"); setError(null); setNotice(null); }}
          className="mt-3 w-full text-center text-[0.84rem] font-bold text-[#8d89a6] hover:text-[#4b33e0]">
          Back to sign in
        </button>
      )}

      <p className="mt-4 text-center text-[0.88rem] text-[#5c5878]">
        New to the program?{" "}
        <Link href="/affiliate/signup" className="font-extrabold text-[#4b33e0] underline-offset-4 hover:underline">Join as an affiliate</Link>
      </p>
    </AuthShell>
  );
}
